import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CompareContent,
  CompareContentWithRequestId,
  loadCompareStateFromStore,
  persistCompareStateToStore,
} from '@neptune/compare-domain';
import { getCurrentProjectIdentifier } from '@neptune/current-project-business-logic';
import { TableViewParams } from '@neptune/shared/leaderboard-domain';

import { getOrganizationNameFromRouteParams } from 'state/selectors-global';
import { setAutoModeOn } from 'state/ui/compare/actions';
import {
  getCompareAutoLimitForCurrentProject,
  getCompareCurrentRouteParams,
  getCompareIdsForCurrentProject,
} from 'state/ui/compare/selectors';

import { updateNavigationCompare } from './update-navigation-compare';
import { useResolveAutoCompare } from './use-resolve-auto-compare';

const EMPTY_IDENTIFICATION_PAIRS = [[], []];
const EMPTY_COMPARE_KEYS: CompareContent['compare'] = {
  runIds: [],
  experimentNames: [],
};

type CompareQueryData = CompareContent | null | undefined;
type CompareStoreScope = {
  workspaceName: string;
  projectIdentifier: string;
};

function isEmptyCompare(compare: CompareContent['compare']): boolean {
  return compare.runIds.length === 0 && compare.experimentNames.length === 0;
}

function matchesScope(scope: CompareStoreScope, content: CompareContent): boolean {
  return (
    content.workspaceName === scope.workspaceName &&
    content.projectIdentifier === scope.projectIdentifier
  );
}

export function useCompareKeysStore({ tableParams }: { tableParams?: TableViewParams }) {
  const dispatch = useDispatch();
  const requestIdRef = React.useRef(0);
  const pendingRequestIdsRef = React.useRef(new Set<number>());
  const lastCommittedQueryDataRef = React.useRef<CompareQueryData>(undefined);
  const autoCompareLimit = useSelector(getCompareAutoLimitForCurrentProject);
  const queryClient = useQueryClient();
  const workspaceName = useSelector(getOrganizationNameFromRouteParams);
  const projectIdentifier = useSelector(getCurrentProjectIdentifier);
  const decompressedRunIds = useSelector(getCompareIdsForCurrentProject);

  const { runIdentificationPairs, resolved: autoCompareResolved } = useResolveAutoCompare({
    projectIdentifier,
    autoComparePool: autoCompareLimit,
    tableParams,
  });

  const [autoCompareExperimentNames, autoCompareIds] =
    runIdentificationPairs ?? EMPTY_IDENTIFICATION_PAIRS;

  const currentCompareParam = useSelector(getCompareCurrentRouteParams);

  const updateURLState = React.useCallback(
    (compare: string | undefined) => dispatch(updateNavigationCompare(compare)),
    [dispatch],
  );

  const queryKey = React.useMemo(
    () => ['retrieve-compare-key-value-data', workspaceName, projectIdentifier],
    [workspaceName, projectIdentifier],
  );
  const currentScope = React.useMemo(
    () => ({ workspaceName, projectIdentifier }),
    [workspaceName, projectIdentifier],
  );

  React.useEffect(() => {
    pendingRequestIdsRef.current = new Set<number>();
    lastCommittedQueryDataRef.current = undefined;
  }, [workspaceName, projectIdentifier]);

  const {
    mutate,
    isLoading: isMutating,
    variables: mutatingContent,
  } = useMutation({
    mutationFn: persistCompareStateToStore,
    onMutate: async ({ requestId, ...content }: CompareContentWithRequestId) => {
      if (!matchesScope(currentScope, content)) {
        return;
      }

      await queryClient.cancelQueries({ queryKey });

      if (requestId === requestIdRef.current) {
        queryClient.setQueryData(queryKey, content);
      }
    },
    onSuccess: (compareKey, { requestId, ...content }) => {
      if (!matchesScope(currentScope, content) || requestId !== requestIdRef.current) {
        return;
      }

      queryClient.setQueryData(queryKey, content);
      lastCommittedQueryDataRef.current = content;
      updateURLState(isEmptyCompare(content.compare) ? undefined : compareKey);
    },
    onError: (_, { requestId, ...content }) => {
      if (!matchesScope(currentScope, content) || requestId !== requestIdRef.current) {
        return;
      }

      const lastCommittedQueryData = lastCommittedQueryDataRef.current;

      if (lastCommittedQueryData === undefined) {
        queryClient.removeQueries({ queryKey, exact: true });
      } else {
        queryClient.setQueryData(queryKey, lastCommittedQueryData);
      }
    },
    onSettled: (_, __, { requestId, ...content }) => {
      if (matchesScope(currentScope, content)) {
        pendingRequestIdsRef.current.delete(requestId);
      }
    },
  });

  const { data: retrievedData, isFetched } = useQuery({
    queryKey,
    queryFn: () => loadCompareStateFromStore(currentCompareParam),
    enabled:
      !autoCompareLimit &&
      currentCompareParam !== undefined &&
      !(isMutating && mutatingContent && matchesScope(currentScope, mutatingContent)),
  });

  const saveCompareState = React.useCallback(
    async (runIds: string[], experimentNames: string[]) => {
      requestIdRef.current++;
      const requestId = requestIdRef.current;

      if (pendingRequestIdsRef.current.size === 0) {
        lastCommittedQueryDataRef.current = queryClient.getQueryData<CompareContent | null>(
          queryKey,
        );
      }

      pendingRequestIdsRef.current.add(requestId);

      mutate({
        requestId,
        workspaceName,
        projectIdentifier,
        compare: {
          runIds,
          experimentNames,
        },
      });
    },
    [workspaceName, projectIdentifier, mutate, queryClient, queryKey],
  );

  const onAutoModeChange = React.useCallback(
    (autoCompareLimit?: number) => {
      if (autoCompareLimit === undefined) {
        saveCompareState(autoCompareIds, autoCompareExperimentNames);
      } else {
        dispatch(setAutoModeOn(autoCompareLimit));
      }
    },
    [autoCompareExperimentNames, autoCompareIds, dispatch, saveCompareState],
  );

  const compareKeys = React.useMemo(() => {
    if (retrievedData) {
      return retrievedData.compare;
    }

    if (!retrievedData && isFetched) {
      return { runIds: decompressedRunIds, experimentNames: EMPTY_COMPARE_KEYS.experimentNames };
    }

    return EMPTY_COMPARE_KEYS;
  }, [decompressedRunIds, isFetched, retrievedData]);

  if (autoCompareLimit) {
    return {
      compareKeys: { runIds: autoCompareIds, experimentNames: autoCompareExperimentNames },
      autoCompareLimit,
      autoCompareResolved,
      onAutoModeChange,
      saveCompareState,
    };
  }

  return {
    compareKeys,
    autoCompareLimit,
    autoCompareResolved,
    onAutoModeChange,
    saveCompareState,
  };
}
